/**
 * career/models.ts — types for Module #7, the Roamer Career™.
 *
 * A platform MODULE, not a core engine. An intelligent career companion —
 * career profile, skills intelligence, income intelligence, client
 * management, work insights, career goals, professional analytics,
 * achievements, and privacy controls. Honesty rules as everywhere: unknown
 * ⇒ null, every insight/metric derives from recorded data with an explicit
 * basis, market-demand facts require a named source, and forecasts are
 * labeled projections that need real history — never fabricated numbers.
 */

// ─── Career profile ────────────────────────────────────────────────────────────

export type CareerStatus = 'available' | 'booked' | 'openToOffers' | 'onBreak' | 'unknown';

export interface ResumeVersion {
  id: string;
  label: string; // e.g. "Design-focused 2026"
  /** A storage reference/URL — never file contents. */
  ref: string;
  updatedAt: number;
}

export interface CareerProfile {
  primaryProfession: string | null;
  yearsExperience: number | null;
  languages: string[];
  certifications: string[];
  portfolioLinks: string[];
  /** When the portfolio was last touched; drives the update nudge. */
  portfolioUpdatedAt: number | null;
  resumeVersions: ResumeVersion[];
  preferredIndustries: string[];
  preferredClients: string[];
  hourlyRate: number | null;
  targetHourlyRate: number | null;
  monthlyIncomeGoal: number | null;
  currency: string;
  availabilityHoursPerWeek: number | null;
  remoteExperienceYears: number | null;
  currentStatus: CareerStatus;
}

export const DEFAULT_CAREER_PROFILE: CareerProfile = {
  primaryProfession: null,
  yearsExperience: null,
  languages: [],
  certifications: [],
  portfolioLinks: [],
  portfolioUpdatedAt: null,
  resumeVersions: [],
  preferredIndustries: [],
  preferredClients: [],
  hourlyRate: null,
  targetHourlyRate: null,
  monthlyIncomeGoal: null,
  currency: 'USD',
  availabilityHoursPerWeek: null,
  remoteExperienceYears: null,
  currentStatus: 'unknown',
};

// ─── Skills intelligence ───────────────────────────────────────────────────────

export interface CareerSkill {
  name: string;
  /** Verified means backed by evidence the user recorded (cert, review, test). */
  verified: boolean;
  verificationNote: string | null;
  /** The user wants to learn/deepen this skill. */
  learningGoal: boolean;
  addedAt: number;
}

/** A market observation the user or a future provider recorded — sourced, never invented. */
export interface MarketSignal {
  id: string;
  kind: 'inDemandSkill' | 'trendingTechnology' | 'recommendedCertification';
  name: string;
  note: string;
  /** Named origin (report, job board, community post). Required. */
  source: string;
  recordedAt: number;
}

export interface SkillsIntelligence {
  verifiedSkills: string[];
  unverifiedSkills: string[];
  learningGoals: string[];
  /** Recorded in-demand skills the user already has. */
  strengths: string[];
  /** Recorded in-demand skills the user lacks — the honest gap list. */
  skillGaps: string[];
  trendingTechnologies: string[];
  recommendedCertifications: string[];
  /** null when no market signals are recorded — no fabricated market wisdom. */
  marketDataAvailable: boolean;
}

// ─── Income intelligence ───────────────────────────────────────────────────────

export type IncomeSource = 'project' | 'mission' | 'payment' | 'other';

export interface IncomeRecord {
  id: string;
  amount: number;
  currency: string;
  source: IncomeSource;
  clientId: string | null;
  /** Hours worked for this income, if recorded — enables hourly averages. */
  hoursWorked: number | null;
  note: string;
  at: number;
}

export interface MonthlyEarnings {
  /** UTC year-month, e.g. "2026-07". */
  month: string;
  total: number;
  recordCount: number;
}

export interface IncomeIntelligence {
  currency: string;
  totalRecorded: number;
  monthlyEarnings: MonthlyEarnings[];
  /** Needs recorded hours; null otherwise. */
  hourlyAverage: number | null;
  projectIncome: number;
  missionIncome: number;
  /** rising | falling | flat from ≥2 full months; null under that. */
  trend: 'rising' | 'falling' | 'flat' | null;
  /** stable | volatile from monthly coefficient of variation; needs ≥3 months. */
  stability: 'stable' | 'volatile' | null;
  /** Month name → average share, only with ≥12 months of data; null otherwise. */
  seasonalityNote: string | null;
  /**
   * A labeled projection (average of recorded months), only with ≥3 months
   * of history. Never presented as a promise.
   */
  forecastNextMonth: number | null;
}

// ─── Client management ─────────────────────────────────────────────────────────

export type ClientStatus = 'active' | 'past';

export interface CareerClient {
  id: string;
  name: string;
  status: ClientStatus;
  favorite: boolean;
  country: string | null;
  /** 1–5 if the user recorded one. */
  rating: number | null;
  relationshipNotes: string;
  addedAt: number;
}

export interface CareerProject {
  id: string;
  clientId: string | null;
  title: string;
  /** Skills used — powers "most profitable skills". */
  skills: string[];
  income: number | null;
  currency: string;
  /** Whether the proposal/application was accepted (for acceptance rate). */
  accepted: boolean | null;
  completedAt: number | null;
  addedAt: number;
}

/** Invoices and contracts are FUTURE scope — reference types only. */
export interface InvoiceRef {
  id: string;
  clientId: string;
  ref: string;
}
export interface ContractRef {
  id: string;
  clientId: string;
  ref: string;
}

// ─── Work insights ─────────────────────────────────────────────────────────────

export interface CareerInsight {
  /** Deterministic — stable while the condition holds. */
  id: string;
  message: string;
  /** The recorded data it derives from. Never empty. */
  basis: string[];
  severity: 'info' | 'nudge' | 'important';
}

// ─── Career goals ──────────────────────────────────────────────────────────────

export const CAREER_GOAL_TYPES = [
  'income',
  'client',
  'certification',
  'learning',
  'promotion',
  'freelance',
  'business',
] as const;
export type CareerGoalType = (typeof CAREER_GOAL_TYPES)[number];

/** Only income goals auto-track (from recorded income); the rest are manual. */
export const AUTO_TRACKED_CAREER_GOAL_TYPES: CareerGoalType[] = ['income'];

export interface CareerGoal {
  id: string;
  type: CareerGoalType;
  title: string;
  targetAmount: number;
  currentAmount: number;
  targetDate: number | null;
  createdAt: number;
  completedAt: number | null;
}

// ─── Professional analytics ────────────────────────────────────────────────────

export interface ProfessionalAnalytics {
  projectsCompleted: number;
  /** accepted / (accepted + rejected); null when no accept/reject data. */
  acceptanceRate: number | null;
  averageProjectEarnings: number | null;
  /** From recorded client ratings only. */
  averageClientRating: number | null;
  ratedClientCount: number;
  bestPayingClients: { clientId: string; name: string; total: number }[];
  mostProfitableSkills: { skill: string; total: number }[];
  /** Recorded-only metrics — null until a future integration records them. */
  averageResponseTimeHours: number | null;
  /** more | less | steady comparing last vs previous month's completions; null under 2 months. */
  productivityTrend: 'more' | 'less' | 'steady' | null;
}

// ─── Achievements (once-only, evidence-based) ──────────────────────────────────

export const CAREER_ACHIEVEMENTS = [
  'firstRemoteJob',
  'earned1000',
  'earned10000',
  'projects100',
  'fiveRepeatClients',
  'topRatedFreelancer',
  'skillMaster',
  'globalProfessional',
] as const;
export type CareerAchievementId = (typeof CAREER_ACHIEVEMENTS)[number];

export interface CareerAchievement {
  id: CareerAchievementId;
  title: string;
  unlockedAt: number;
  /** What earned it — recorded evidence, not vibes. */
  evidence: string;
}

// ─── AI coaching (future — interface only) ─────────────────────────────────────

export interface CareerCoach {
  id: string;
  name: string;
  coach(prompt: string): Promise<CareerInsight[]>;
}

// ─── Privacy ───────────────────────────────────────────────────────────────────

/** Private by default — everything shared must be opted into. */
export interface CareerPrivacySettings {
  profileVisible: boolean;
  portfolioSharingEnabled: boolean;
  resumeSharingEnabled: boolean;
  incomeVisible: boolean;
  clientInfoVisible: boolean;
  analyticsVisible: boolean;
}

export const DEFAULT_CAREER_PRIVACY: CareerPrivacySettings = {
  profileVisible: false,
  portfolioSharingEnabled: false,
  resumeSharingEnabled: false,
  incomeVisible: false,
  clientInfoVisible: false,
  analyticsVisible: false,
};

// ─── Export ────────────────────────────────────────────────────────────────────

export interface CareerExport {
  exportedAt: number;
  /** Sections gated by privacy settings are null/empty when not opted in. */
  profile: CareerProfile | null;
  skills: CareerSkill[];
  marketSignals: MarketSignal[];
  incomeRecords: IncomeRecord[];
  clients: CareerClient[];
  projects: CareerProject[];
  goals: CareerGoal[];
  achievements: CareerAchievement[];
}
